import * as ids from 'short-id';
import { omit } from 'underscore';
import { TrackType } from 'types';
import { canDeleteTrack, getNewTrackIndex } from 'utils/embed/tracks';
import { changeElementPositionInArray } from 'utils/list';
import { LayerState, LayerType, VideoTemplateStateContent } from '../types';

export function canDeleteLayer(
  { layers, originalLayers, template }: VideoTemplateStateContent,
  layerId: string,
): boolean {
  return (
    canDeleteTrack(layers.data, layerId) &&
    (!template || !originalLayers.includes(layerId))
  );
}

/**
 * Obtains the count of remaining assets that will remain in a particular layer after deleting
 * an asset
 * @param {object} state - Current template editor's state
 * @param {string} layerId - Target layer id.
 * @returns {number} - The amount of remaining assets for the target layer
 */
export const getRemainingAssetCountInLayer = (
  state: VideoTemplateStateContent,
  layerId: string,
): number => {
  const { slideshow, videoClips } = state;

  const slideLayerCount = slideshow.order
    .map(slideId => slideshow.data[slideId].layerId)
    .filter(slideLayerId => slideLayerId === layerId).length;
  const videoLayerCount = videoClips.order
    .map(videoId => videoClips.data[videoId].layerId)
    .filter(videoLayerId => videoLayerId === layerId).length;

  const nAssetsLeftInLayer = slideLayerCount + videoLayerCount - 1;

  return nAssetsLeftInLayer;
};

/**
 * Moves a layer to a specific index in the layer order array.
 *
 * @param layers - The current layer state.
 * @param layerId - The ID of the layer to move.
 * @param targetLayerIndex - The target index where the layer should be moved.
 *
 * @returns Updated layer state with the layer repositioned.
 */
export function moveLayerToIndex(
  layers: LayerState,
  layerId: string,
  targetLayerIndex: number,
): LayerState {
  const layerOrder = [...layers.order];
  const currentIndex = layerOrder.indexOf(layerId);

  if (currentIndex === -1) {
    return layers;
  }

  const newOrder = changeElementPositionInArray(
    layerOrder,
    currentIndex,
    targetLayerIndex,
  );

  return {
    ...layers,
    order: newOrder,
  };
}

function getUnusedLayer(
  state: VideoTemplateStateContent,
  type: TrackType,
): string {
  const { layers } = state;
  const reversedLayers = [...layers?.order].reverse();

  if (type === 'text') {
    const { textOverlays } = state;
    return reversedLayers.find(layerId => {
      const layer = layers.data[layerId];

      // skip non-text layers since those can't be used for text assets
      if (layer.type !== 'text') return false;

      // check if there are any text overlays using the id.  if not, this id is
      // considered unused
      return textOverlays.order.every(overlayId => {
        const overlay = textOverlays.data[overlayId];
        return overlay.layerId !== layerId;
      });
    });
  }

  if (type === 'media') {
    const { slideshow, videoClips } = state;
    return reversedLayers.find(layerId => {
      const layer = layers.data[layerId];

      // skip non-media layers since those can't be used for media assets
      if (layer.type !== 'media') return false;

      // check if any slides are using this layer
      const unusedForSlideshow = slideshow.order.every(slideId => {
        const slide = slideshow.data[slideId];
        return slide.layerId !== layerId;
      });

      const unusedForVideo = videoClips.order.every(videoId => {
        const videoClip = videoClips.data[videoId];
        return videoClip.layerId !== layerId;
      });

      return unusedForSlideshow && unusedForVideo;
    });
  }

  if (type === 'waveform') {
    const { soundwave } = state;

    return reversedLayers.find(layerId => {
      const layer = layers.data[layerId];

      if (layer.type !== 'waveform') return false;

      return layerId !== soundwave?.layerId;
    });
  }
  return undefined;
}

/**
 * creates a new layer maintaining the track ordering logic
 */
export function createNewLayer(
  state: VideoTemplateStateContent,
  type: LayerType,
  targetLayerId?: string,
  targetLayerIndex?: number,
): [string, LayerState] {
  // if a target layer id is specified, no new layers are created and curr layers + target id
  // are returned.
  if (targetLayerId) {
    return [targetLayerId, state.layers];
  }

  const unusedLayerId = getUnusedLayer(state, type);

  if (unusedLayerId) {
    return [unusedLayerId, state.layers];
  }

  const {
    layers: { order, data },
  } = state;
  const newIndex = getNewTrackIndex(order, data, type, 'free');
  const id = ids.generate();

  const orderCopy = [...order];

  // no track exists where this one should go
  if (orderCopy[newIndex] === undefined) {
    orderCopy[newIndex] = id;
  } else {
    // a track exists where this one should go, so splice it in
    orderCopy.splice(newIndex, 0, id);
  }

  const layers = {
    data: {
      ...data,
      [id]: { id, type },
    },
    order: orderCopy,
  };

  const finalLayers =
    targetLayerIndex !== undefined
      ? moveLayerToIndex(layers, id, targetLayerIndex)
      : layers;

  return [id, finalLayers];
}

export function deleteLayer(
  state: VideoTemplateStateContent,
  id: string,
): Pick<VideoTemplateStateContent, 'layers'> {
  const { layers } = state;

  const canDelete = canDeleteLayer(state, id);

  return {
    layers: !canDelete
      ? layers
      : {
          data: omit(layers.data, id),
          order: layers.order.filter(layerId => layerId !== id),
        },
  };
}
